import { useState } from 'react';

import { useBreakPoints, usePlatformService } from 'core';
import { DownloadGivebacksAppQRCodeModal } from 'core/Components';
import {
  ButtonMantine,
  Container,
  Grid,
  Group,
  MobileBottomButton,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from 'ui';
import { IconBling, LogoGivebacksSupporterApp } from 'ui/Icons';

type AppBenefitProps = {
  children: string;
};

const AppBenefit = ({ children }: AppBenefitProps) => {
  return (
    <Group wrap="nowrap" gap="xs">
      <ThemeIcon variant="transparent" size="xs">
        <IconBling />
      </ThemeIcon>
      <Text fz="sm" c="gray">
        {children}
      </Text>
    </Group>
  );
};

type AppBenefitsProps = {
  children: React.ReactNode;
};

const AppBenefits = ({ children }: AppBenefitsProps) => {
  return (
    <Stack gap="sm">
      <Group wrap="nowrap" gap={6}>
        <Text fz="sm" c="gray">
          With the
        </Text>
        <LogoGivebacksSupporterApp w={81} mb={-3} />
        <Text fz="sm" c="gray">
          app you can:
        </Text>
      </Group>

      {children}
    </Stack>
  );
};

type PostCheckoutRedirectPageTemplateProps = {
  title: string;
  leftSection: React.ReactNode;
  rightSection: React.ReactNode;
  cta: React.ReactNode;
};

const PostCheckoutRedirectPageTemplate = ({
  title,
  leftSection,
  rightSection,
  cta,
}: PostCheckoutRedirectPageTemplateProps) => {
  const { isSmallScreen } = useBreakPoints();
  const [isGetAppModalOpen, setIsGetAppModalOpen] = useState(false);

  const Platform = usePlatformService();

  return (
    <Container size="xl" mb={{ base: 136, sm: 0 }} mt="xl">
      <Grid gutter={{ base: 'lg', sm: 'xl' }}>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Stack gap={isSmallScreen ? 'lg' : 'xl'}>
            <Title order={2}>{title}</Title>
            {isSmallScreen ? rightSection : null}

            {/* left section */}
            {leftSection}
            {!isSmallScreen && (
              <Stack gap="xs">
                {cta}
                <ButtonMantine
                  variant="outline"
                  onClick={() => {
                    if (Platform.mobileBrowser) {
                      return window.open('https://api.givebacks.com/magic', '_blank');
                    }

                    setIsGetAppModalOpen(true);
                  }}
                >
                  Get the App
                </ButtonMantine>
              </Stack>
            )}
          </Stack>
        </Grid.Col>

        {!isSmallScreen && (
          <Grid.Col span={5} offset={1}>
            {rightSection}
          </Grid.Col>
        )}
      </Grid>

      <MobileBottomButton mobileBreakpoint="sm">
        <Stack gap="xs">
          {cta}
          <ButtonMantine
            variant="outline"
            onClick={() => {
              if (Platform.mobileBrowser) {
                return window.open('https://api.givebacks.com/magic', '_blank');
              }

              setIsGetAppModalOpen(true);
            }}
          >
            Get the App
          </ButtonMantine>
        </Stack>
      </MobileBottomButton>

      <DownloadGivebacksAppQRCodeModal
        opened={isGetAppModalOpen}
        onClose={() => setIsGetAppModalOpen(false)}
      />
    </Container>
  );
};

PostCheckoutRedirectPageTemplate.AppBenefits = AppBenefits;
PostCheckoutRedirectPageTemplate.AppBenefit = AppBenefit;

export { PostCheckoutRedirectPageTemplate };
